import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { cn } from "@/lib/cn";

interface Props {
  descriptionEs: string;
  descriptionEn: string;
  className?: string;
}

// Small "i" affordance next to a nav item: tap/click to reveal what that
// screen does. Deliberately click-toggled rather than hover-only, since
// residents use this from phones.
export function InfoPopover({ descriptionEs, descriptionEn, className }: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        aria-label={isEn ? "What does this screen do?" : "¿Qué hace esta pantalla?"}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center p-1 rounded text-primary-200 hover:text-white hover:bg-primary-700 transition"
      >
        <Info size={14} />
      </button>
      {open && (
        <div
          id={id}
          role="tooltip"
          className="absolute z-50 left-full top-1/2 -translate-y-1/2 ml-2 w-56 p-3 bg-white text-slate-700 text-xs leading-relaxed rounded-md shadow-elegant-lg border border-slate-200"
        >
          {isEn ? descriptionEn : descriptionEs}
        </div>
      )}
    </div>
  );
}
